/**
 * niceScale (#2590) — pure "nice number" rounding for a LINEAR VALUE axis: the
 * y-axis counterpart of {@link ./timeTicks}' calendar ticks.
 *
 * ── Why a chart needs this at all ──────────────────────────────────────────
 * recharts computes nice ticks only for a ``domain`` that still contains
 * ``'auto'``. Give ``<YAxis>`` an explicit ``[min, max]`` and it takes the span
 * literally and divides it into ``tickCount - 1`` equal parts, so a domain of
 * ``[4.2, 7]`` labels as ``4,2 / 4,9 / 5,6 / 6,3 / 7`` — arbitrary offsets from
 * wherever the data happened to start.
 *
 * That matters because #2590 has to REPLACE ``'auto'``: with ``'auto'`` the
 * scale is derived from the chart's ``data`` array, which is deliberately
 * edge-PADDED (the nearest row outside each edge, so a line entering the
 * viewport reaches the plot edge). Zoom next to an off-screen spike and the axis
 * stretches to a value nothing on the plot explains. Scaling to the VISIBLE rows
 * means an explicit domain, and an explicit domain means bringing the rounding.
 *
 * ── What "nice" means here ─────────────────────────────────────────────────
 * The classic Heckbert construction: pick a step of 1, 2 or 5 times a power of
 * ten near ``range / (count - 1)``, then round the ends OUTWARD to whole
 * multiples of it. Outward is the load-bearing half — the axis is drawn with
 * ``allowDataOverflow``, which CLIPS anything outside the domain, so an inward
 * round would hide the very observation the axis exists to show.
 *
 * The 1/2/5 ladder is deliberately narrower than d3's (which also offers 2.5):
 * every step is then exactly representable at its own decimal place, so the
 * ticks can be de-dusted by decimal rounding and ``0.30000000000000004`` can
 * never reach a ``tickFormatter``.
 *
 * Pure — no React, no recharts — so the invariants are unit-tested directly.
 */

/** A rounded value-axis range plus the ticks that divide it. */
export interface NiceLinearScale {
  /** Lower bound, at or below the requested ``lo``. */
  min: number;
  /** Upper bound, at or above the requested ``hi``. */
  max: number;
  /**
   * Ascending tick positions, evenly spaced, starting at {@link min} and ending
   * at {@link max}.
   *
   * Emitted rather than left to recharts because the equal-division fallback
   * described above applies to the TICKS too: a nice domain alone would still be
   * chopped into ``(max - min) / (tickCount - 1)`` pieces, which for ``[4, 7]``
   * is ``4 / 4,75 / 5,5 / 6,25 / 7``. Both halves have to be pinned.
   */
  ticks: number[];
  /**
   * Decimal places needed to print every tick DISTINCTLY, derived from the step.
   *
   * Surfaced because a caller's own formatter cannot know it: a price axis
   * formatted at two decimals is fine at a 0,50 step and broken at a 0,005 one,
   * where 10,385 and 10,39 both print as "10,39". That is not hypothetical for a
   * windowed axis — narrowing the range is the whole point, so fine steps are the
   * normal outcome of a moderate zoom on a stable series.
   */
  decimals: number;
}

/**
 * Target label count — ~5 reads comfortably on the ~294 px plot the app charts
 * use. It is a TARGET, not a guarantee: the count moves by a label or two as the
 * step is snapped onto the 1/2/5 ladder and the ends are rounded outward, which
 * is the trade that buys round numbers.
 */
export const DEFAULT_NICE_TICK_COUNT = 5;

/**
 * Runaway guard on the tick loop, derived rather than picked.
 *
 * Snapping the step onto the ladder can only ever SHRINK it by a factor under
 * 1.5 (the worst rung transition is a mantissa of 1.5 rounding down to 1), and
 * rounding both ends outward adds under two more intervals — so the loop cannot
 * legitimately produce more than this. For the default count it evaluates to 9,
 * the band the unit tests pin. It exists so a hostile input cannot spin, never
 * as a policy limit: a caller asking for 11 labels gets 11.
 */
function maxTicksFor(count: number): number {
  return Math.ceil((count - 1) * 1.5) + 3;
}

/**
 * How far a flat range is opened up, as a fraction of the value's own magnitude.
 *
 * A series that never moves (one price all window) has a zero-height extent, and
 * a zero-height domain renders as an empty plot. Relative rather than absolute so
 * a 0,50 €/kg item and a 30 €/kg one both get a band that reads as "flat" rather
 * than as noise; a flat ZERO series has no magnitude to scale by and falls back
 * to ±{@link FLAT_ZERO_PAD}.
 */
const FLAT_PAD_FRACTION = 0.1;
const FLAT_ZERO_PAD = 0.1;

/**
 * The nearest 1/2/5/10 × 10^k to ``x`` (``x`` > 0).
 *
 * ``toPrecision`` before comparing: ``0.7 / 0.1`` is ``6.999999999999999`` in
 * IEEE 754, which would pick the 5-step ladder rung instead of the 10-step one
 * and make the chosen step depend on float dust rather than on the data.
 */
function niceStep(x: number): number {
  const exp = Math.floor(Math.log10(x));
  const f = Number((x / 10 ** exp).toPrecision(12));
  const mantissa = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  return mantissa * 10 ** exp;
}

/**
 * Decimal places a tick needs, derived from the step.
 *
 * Exact BECAUSE the ladder is 1/2/5/10: every rung shares its power-of-ten
 * exponent, so one decimal place per negative exponent represents each of them
 * without loss. (A 2.5 rung would break this — hence its absence.)
 */
function decimalsFor(step: number): number {
  return Math.min(20, Math.max(0, -Math.floor(Math.log10(step))));
}

/**
 * Round ``lo``…``hi`` outward onto a 1/2/5 grid and emit the ticks.
 *
 * Returns ``null`` for a non-finite input — notably ``±Infinity``, which is what
 * ``Math.min``/``Math.max`` over an EMPTY set produce. That value must never
 * reach ``<YAxis domain=…>`` (recharts renders an empty plot), so the caller is
 * forced to decide: fall back to ``['auto', 'auto']`` rather than pass garbage.
 * An inverted range is normalised rather than rejected — the ORDER of two
 * extremes is never the caller's meaning.
 */
export function niceLinearScale(lo: number, hi: number, opts?: { count?: number }): NiceLinearScale | null {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;

  const requested = opts?.count;
  // A count below 2 divides by zero (or loops); anything non-finite is garbage.
  // Degrade to the default rather than throw — a mis-configured axis should
  // still draw.
  const count =
    requested != null && Number.isFinite(requested) && requested >= 2 ? Math.floor(requested) : DEFAULT_NICE_TICK_COUNT;

  let from = Math.min(lo, hi);
  let to = Math.max(lo, hi);
  // RELATIVE, not `from === to`. Two prices that are "the same" are routinely not
  // bit-identical: `aggregate`'s percentiles INTERPOLATE, so a day aggregated from
  // {2,00; 2,06} yields a median of `2.0300000000000002` against a plain `2.03`
  // elsewhere. An exact-equality guard lets that through as a range 4e-16 wide,
  // and a domain that height — handed to an axis with `allowDataOverflow` —
  // pins the two to opposite edges of the plot and draws a dramatic swing
  // between two values that print identically. That is precisely the
  // "chart misleads the reader" failure this whole area exists to remove.
  //
  // Padded around the MIDPOINT so a near-flat range is centred, exactly as an
  // exactly-flat one is (where `mid === from === to`).
  if (!(to - from > Math.max(Math.abs(from), Math.abs(to)) * 1e-9)) {
    const mid = (from + to) / 2;
    const pad = Math.abs(mid) * FLAT_PAD_FRACTION || FLAT_ZERO_PAD;
    from = mid - pad;
    to = mid + pad;
  }

  const step = niceStep((to - from) / (count - 1));
  const dp = decimalsFor(step);
  const round = (v: number): number => Number(v.toFixed(dp));

  // Index arithmetic rather than repeated addition: `min + i * step` accumulates
  // no error, and every tick is a whole multiple of the step by construction.
  //
  // `snap` removes float dust from the quotient BEFORE rounding outward: `5.4 /
  // 0.1` is `54.00000000000001`, so a raw `Math.ceil` would step the top of the
  // axis to 5,5 for data that ends exactly on 5,4 — a whole extra tick, decided
  // by a representation artefact. The tolerance is deliberately far below any
  // real value's resolution (1e-12 relative), so a quotient that is genuinely
  // just past an integer still rounds outward and the domain still contains the
  // data — the property the axis' `allowDataOverflow` depends on.
  //
  // ABSOLUTELY capped as well: the tolerance is relative to the QUOTIENT, so at
  // |q| above ~5e11 it would exceed half a step and start collapsing genuinely
  // fractional positions onto integers — i.e. rounding INWARD and clipping real
  // data. 1e-6 of a step is far above any dust and far below half of one.
  const snap = (q: number): number => {
    const r = Math.round(q);
    return Math.abs(q - r) < Math.min(1e-6, 1e-12 * Math.max(1, Math.abs(q))) ? r : q;
  };
  const firstIndex = Math.floor(snap(from / step));
  const lastIndex = Math.ceil(snap(to / step));
  const min = round(firstIndex * step);
  const max = round(lastIndex * step);

  // The module's own contract: a non-finite or degenerate domain must never reach
  // `<YAxis domain=…>`. Reachable only from absurd inputs (`Number.MAX_VALUE`
  // overflows the pad to ±Infinity; a range under ~1e-20 rounds to a point once
  // `dp` saturates at 20), but guarding the OUTPUT rather than only the input is
  // what makes the contract true rather than merely intended.
  if (!Number.isFinite(min) || !Number.isFinite(max) || !(max > min)) return null;

  const ticks: number[] = [];
  const maxTicks = maxTicksFor(count);
  for (let i = firstIndex; i <= lastIndex && ticks.length < maxTicks; i += 1) {
    ticks.push(round(i * step));
  }
  return { min, max, ticks, decimals: dp };
}
